import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { ToastUtils } from '@ocean/utils'
import { AppThunk } from 'store'
import { createApiAction } from 'api'
import { LocalizationUtils } from 'utils'
import { userLoggedOut } from 'core/session/slices/session'
import { runCesPlayer, playerClose } from 'core/staIntegration/slices/staLegacyPlayer'
import {
  getCesDetails,
  getCesLockout,
} from 'modules/activityDetails/CesStcwDetails/api/cesStcwDetailsService'
import {
  CesStcwDetailsState,
  ApiCesStcwDetailsResponse,
  ApiCesStcwLockoutResponse,
  RunCesParams,
} from './types'

const initialState: CesStcwDetailsState = {
  cesStcwDetailsData: {},
  cesStcwLockoutData: {},
  forceReload: false,
  testId: null,
}

const cesStcwDetailsSlice = createSlice({
  name: 'cesStcwDetails',
  initialState,
  reducers: {
    cesStcwDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiCesStcwDetailsResponse>>
    ) {
      state.cesStcwDetailsData = action.payload
      state.forceReload = false

      const { cesStcwDetails } = action.payload.result || {}
      state.testId = cesStcwDetails?.details.testId || null
    },
    clearCesStcwDetails(state) {
      state.cesStcwDetailsData = initialState.cesStcwDetailsData
    },
    cesStcwLockoutLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiCesStcwLockoutResponse>>
    ) {
      state.cesStcwLockoutData = action.payload
    },
  },
  extraReducers: builder => {
    builder
      .addCase(userLoggedOut, () => {
        return initialState
      })
      .addCase(playerClose, state => {
        state.forceReload = true
        return state
      })
  },
})

const { cesStcwDetailsLoaded, clearCesStcwDetails, cesStcwLockoutLoaded } =
  cesStcwDetailsSlice.actions

export { clearCesStcwDetails }
export default cesStcwDetailsSlice.reducer

export const loadCesStcwDetailsData = (cesId: Guid, resultId?: Guid) => {
  return createApiAction({
    apiCalls: {
      cesStcwDetails: () => getCesDetails(cesId, resultId),
    },
    payloadAction: cesStcwDetailsLoaded,
  })
}

export const reloadCesStcwDetailsData = (cesId: Guid, resultId?: Guid) => {
  return createApiAction({
    apiCalls: {
      cesStcwDetails: () => getCesDetails(cesId, resultId),
    },
    payloadAction: cesStcwDetailsLoaded,
    shouldShowGlobalLoading: true,
    shouldCallApi: ({ cesStcwDetails }) => !cesStcwDetails.cesStcwDetailsData.isLoading,
    getResultToKeep: ({ cesStcwDetails }) => cesStcwDetails.cesStcwDetailsData.result,
  })
}

export const openCes =
  (scheduleId?: Guid): AppThunk =>
  async (dispatch, getState) => {
    const { testId } = getState().cesStcwDetails
    if (!testId) return ToastUtils.error(LocalizationUtils.localizeError('applicationError'))
    dispatch(runCesPlayer(testId, scheduleId))
  }

const runCes =
  (response: ApiMiddlewarePayload<ApiCesStcwLockoutResponse>, params: RunCesParams): AppThunk =>
  async dispatch => {
    dispatch(cesStcwLockoutLoaded(response))
    const { error, result } = response
    const { scheduleId } = params

    if (!error && !result?.cesStcwLockout.isLockedOut) dispatch(openCes(scheduleId))
  }

export const loadCesStcwLockoutData = (cesId: Guid, scheduleId?: Guid) => {
  return createApiAction({
    apiCalls: {
      cesStcwLockout: () => getCesLockout(cesId),
    },
    payloadAction: cesStcwLockoutLoaded,
    nextAction: {
      action: runCes,
      params: { scheduleId },
    },
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}
